import { useState } from 'react';

import { Button } from '@components/button';
import { CollapseCard } from '@components/card/collapse-card';
import { createForm, useCreateFormStore } from '@components/form';
import { useDeleteModal } from '@components/generic-modals';
import { IconFancyContainer } from '@components/icon-fancy-container';
import { IconApi } from '@components/icons/api';
import { IconDuplicate } from '@components/icons/duplicate';
import { IconEdit } from '@components/icons/edit';
import { IconMoreVertical } from '@components/icons/more-vertical';
import { IconOutput } from '@components/icons/output';
import { IconRemove } from '@components/icons/remove';
import { IconSend } from '@components/icons/send';
import { Menu } from '@components/menu';
import { MenuItem } from '@components/menu/menu';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { lineClamp } from '@styles/layout-helpers';
import { pxToRem } from '@utils/converters';

import {
  FieldTable,
  MethodTag,
  generateTemplateName,
  getFormEndpointVariables,
  getVariableCount,
  methodFancyContainerColorMap,
} from './common';
import { endpointContext } from './endpoint-fields';
import {
  useDeleteIntegrationEndpoint,
  useUpdateIntegrationEndpoint,
  useUpdateIntegrationEndpointDefaultFiles,
  useUpdateIntegrationEndpointExampleFiles,
} from './integrations-hooks';
import type { IntegrationEndpoint, IntegrationEndpointVariable } from './integrations-types';
import { useDuplicateIntegrationEndpointModal } from './modals/duplicate-endpoint';
import { useTestRequestModal } from './modals/test-request';
import { ResponseValueCard } from './response-values';
import { useIntegrationEndpointCreateSidebar } from './sidebars/add-edit-endpoint';

type EndpointCardProps = {
  endpoint: IntegrationEndpoint<string>;
};

export const EndpointCard = ({ endpoint }: EndpointCardProps) => {
  const { t } = useScopedTranslate(locale);
  const [responseValuesCollapsed, setResponseValuesCollapsed] = useState(true);
  const integrationId = endpoint.integration;
  const showEditSidebar = useIntegrationEndpointCreateSidebar();
  const showDuplicateModal = useDuplicateIntegrationEndpointModal();
  const showTestRequestModal = useTestRequestModal();
  const showDeleteModal = useDeleteModal();
  const deleteMutation = useDeleteIntegrationEndpoint(endpoint.integration, endpoint.shortId);

  return (
    <CollapseCard
      keepCollapsedInDom
      defaultCollapsed
      space={4}
      variant="white"
      key={endpoint.shortId}
      renderCollapse={() => (
        <Stack space={2}>
          <RequestValuesCard
            endpoint={endpoint}
            onTestRequest={() => showTestRequestModal({ endpointId: endpoint.shortId })}
          />
          <ResponseValueCard
            endpoint={endpoint}
            defaultCollapsed={responseValuesCollapsed}
            onVisibilityToggle={setResponseValuesCollapsed}
            key={Object.keys(endpoint.responseValues ?? {}).length}
          />
        </Stack>
      )}
    >
      <Stack>
        <Stack orientation="horizontal" align="center" justify="space-between">
          <Stack space={4} orientation="horizontal" align="center">
            <IconFancyContainer
              variant={methodFancyContainerColorMap[endpoint.method]}
              icon={<IconApi />}
            />
            <div>
              <Text text={endpoint.name} font="semibold14" />
              <Text
                text={endpoint.url}
                style={{
                  ...lineClamp('-webkit-inline-box', 1, true),
                  paddingRight: pxToRem(6),
                }}
                font="regular14"
                color="textHelper"
              />
            </div>
          </Stack>
          <Stack space={4} orientation="horizontal" align="center">
            <MethodTag method={endpoint.method} />
            <Menu buttonProps={{ iconLeft: <IconMoreVertical />, variant: 'ghost' }}>
              <MenuItem
                icon={<IconEdit />}
                label={t('core.edit')}
                onSelect={() => showEditSidebar({ integrationId, endpoint })}
              />
              <MenuItem
                icon={<IconDuplicate />}
                label={t('core.duplicate')}
                onSelect={() => showDuplicateModal({ endpoint })}
              />
              <Menu.Separator />
              <MenuItem
                icon={<IconRemove />}
                label={t('deleteEndpoint')}
                variant="danger"
                onSelect={() =>
                  showDeleteModal({
                    title: t('deleteEndpoint'),
                    description: t('areYouSureDelete'),
                    buttonText: t('deleteEndpoint'),
                    variant: 'LARGE',
                    hideCannotBeUndone: true,
                    onConfirm: () => deleteMutation.mutate(),
                  })
                }
              />
            </Menu>
          </Stack>
        </Stack>
      </Stack>
    </CollapseCard>
  );
};

type RequestValuesCardProps = {
  endpoint: IntegrationEndpoint<string>;
  onTestRequest?: () => void;
};

type Data = {};

const Form = createForm<Data>();

const getDefaultValueFiles = (
  variables: IntegrationEndpointVariable[],
  allVariables: IntegrationEndpointVariable[],
  formData: FormData
) => {
  variables.forEach((variable) => {
    if (variable.type === 'file') {
      const variableName = generateTemplateName(allVariables, variable);
      const value = variable.default_value;
      if (value instanceof File) {
        formData.append(variableName, value as File);
      }
      if (value === null) {
        formData.append(`${variableName}__remove`, '');
      }
      variable.default_value = '';
    }
    if (variable.type === 'array' && variable.template) {
      getDefaultValueFiles(variable.template, allVariables, formData);
    }
  });
};

export const getExampleValueFiles = (
  variables: IntegrationEndpointVariable[],
  allVariables: IntegrationEndpointVariable[],
  formData: FormData
) => {
  variables.forEach((variable) => {
    if (variable.type === 'file') {
      const variableName = generateTemplateName(allVariables, variable);
      const value = variable.example;
      if (value instanceof File) {
        formData.append(variableName, value as File);
      }
      if (value === null) {
        formData.append(`${variableName}__remove`, '');
      }
      variable.example = '';
    }
    if (variable.type === 'array' && variable.template) {
      getExampleValueFiles(variable.template, allVariables, formData);
    }
  });
};

export const RequestValuesCard = ({ endpoint, onTestRequest }: RequestValuesCardProps) => {
  const { t } = useScopedTranslate(locale);
  const formStore = useCreateFormStore();
  const isDirty = formStore.useIsDirty();
  const updateMutation = useUpdateIntegrationEndpoint();
  const defaultFilesMutation = useUpdateIntegrationEndpointDefaultFiles();
  const exampleFileMutation = useUpdateIntegrationEndpointExampleFiles();

  const endpointVariables = endpoint.variables as IntegrationEndpointVariable[];
  const visibleColumns = {
    name: true,
    type: true,
    label: true,
    example: false,
    default: true,
    visible: true,
    format: false,
  };

  const handleUpdateEndpoint = async () => {
    const variables = getFormEndpointVariables({
      formStore,
      endpointVariables,
      visibleColumns,
    });
    const defaultFiles = new FormData();
    getDefaultValueFiles(variables, variables, defaultFiles);
    const exampleFiles = new FormData();
    getExampleValueFiles(variables, variables, exampleFiles);

    await updateMutation.mutateAsync({
      shortId: endpoint.shortId,
      data: {
        variables,
      },
    });

    if (visibleColumns.example && Array.from(exampleFiles.keys()).length > 0) {
      exampleFileMutation.mutateAsync({
        shortId: endpoint.shortId,
        data: exampleFiles,
      });
    }
    if (visibleColumns.default && Array.from(defaultFiles.keys()).length > 0) {
      defaultFilesMutation.mutateAsync({
        shortId: endpoint.shortId,
        data: defaultFiles,
      });
    }
  };
  const formActionPortalId = `${endpoint.shortId}-form-actions-portal`;

  return (
    <endpointContext.Provider value={endpoint}>
      <CollapseCard
        keepCollapsedInDom
        defaultCollapsed
        spaceX={4}
        spaceY={2}
        variant="gray"
        renderCollapse={() => (
          <Form store={formStore} onSubmit={handleUpdateEndpoint}>
            <FieldTable
              form={Form}
              endpointId={endpoint.shortId}
              endpointVariables={endpointVariables}
              visibleColumns={visibleColumns}
              requestType={endpoint.requestType}
            />
            {isDirty && (
              <Form.Actions portal={formActionPortalId}>
                <Stack orientation="horizontal" space={2} align="center">
                  <Form.Reset stopPropagation text={t('core.cancel')} variant="secondary" />
                  <Form.Submit
                    stopPropagation
                    isDisabled={updateMutation.isLoading}
                    text={t('core.save')}
                    variant="primary-blue"
                  />
                </Stack>
              </Form.Actions>
            )}
          </Form>
        )}
      >
        <Stack orientation="horizontal" justify="space-between" align="center">
          <Stack orientation="horizontal" align="center" space={2}>
            <IconOutput color="elementPurple50" />
            <Text text={t('requestValues')} font="semibold14" />
            <Text text="•" color="textHelper" />
            <Text text={getVariableCount(endpointVariables)} font="semibold14" />
          </Stack>
          <Stack orientation="horizontal" space={4} align="center">
            <Stack space={2} orientation="horizontal" align="center">
              {!isDirty && onTestRequest && (
                <Button
                  stopPropagation
                  text={t('test')}
                  iconLeft={<IconSend />}
                  variant="ghost"
                  onClick={onTestRequest}
                />
              )}
              <div id={formActionPortalId} />
            </Stack>
          </Stack>
        </Stack>
      </CollapseCard>
    </endpointContext.Provider>
  );
};

const locale = {
  en: {
    deleteEndpoint: 'Delete Endpoint',
    areYouSureDelete:
      'You are about to permanently delete this endpoint. The following will be affected:\n• All current configurations and response values\n• Journeys that rely on this endpoint\n• Travellers who are currently in progress\n\nOnce deleted, you can recreate this endpoint, but any lost data or configurations cannot be restored.',
    requestValues: 'Request values',
    test: 'Test',
  },
};
